import type { ProviderCondition } from "@/config/energyProvider";
import { STRINGS } from "@/i18n";
import { formatDateLabel } from "./format";

/** Local calendar date as YYYY-MM-DD, comparable with a condition's end date. */
const dateKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

/** Conditions in force on `today`: a fixed formula whose end date has passed is dropped. */
export function activeConditions(
  conditions: ProviderCondition[] | undefined,
  today: Date
): ProviderCondition[] {
  if (!conditions) return [];
  const todayKey = dateKey(today);
  return conditions.filter(
    (condition) =>
      condition.kind !== "fixedFormula" ||
      condition.until === undefined ||
      condition.until >= todayKey
  );
}

export type ProviderFilterId =
  | "discount"
  | "quarterHourly"
  | "noDeposit"
  | "fixedFormula"
  | "noMembership";

/** Display order of the filter buttons. */
export const PROVIDER_FILTERS: readonly ProviderFilterId[] = [
  "discount",
  "quarterHourly",
  "noDeposit",
  "fixedFormula",
  "noMembership",
];

export function matchesFilter(
  conditions: ProviderCondition[],
  filter: ProviderFilterId
): boolean {
  switch (filter) {
    case "discount":
      return conditions.some((condition) => condition.kind === "discount");
    case "quarterHourly":
      return conditions.some(
        (condition) =>
          condition.kind === "settlement" && condition.interval === "15m"
      );
    case "noDeposit":
      return conditions.some((condition) => condition.kind === "noDeposit");
    case "fixedFormula":
      return conditions.some((condition) => condition.kind === "fixedFormula");
    case "noMembership":
      return !conditions.some((condition) => condition.kind === "membership");
  }
}

/**
 * Filters worth offering for a list of providers: each one keeps at least one provider and
 * drops at least one, so a filter never shows up where it cannot change the list.
 */
export function availableFilters(
  conditionsPerProvider: ProviderCondition[][]
): ProviderFilterId[] {
  return PROVIDER_FILTERS.filter((filter) => {
    const matches = conditionsPerProvider.filter((conditions) =>
      matchesFilter(conditions, filter)
    ).length;
    return matches > 0 && matches < conditionsPerProvider.length;
  });
}

/** Supplier name with its product name, for places that list providers on one line. */
export function providerDisplayName(provider: {
  name: string;
  product?: string;
}): string {
  return provider.product ? `${provider.name} · ${provider.product}` : provider.name;
}

export type ConditionIcon = "clock" | "lock" | "document" | "members";

/** What a chip shows: its label, explanation, styling and icon. */
export interface ConditionDescription {
  label: string;
  detail: string;
  /** Positive conditions (bonuses, discounts) get the filled styling. */
  tone: "neutral" | "positive";
  icon: ConditionIcon | null;
}

export function describeCondition(
  condition: ProviderCondition
): ConditionDescription {
  const t = STRINGS.providers;
  switch (condition.kind) {
    case "settlement":
      return {
        label: condition.interval === "15m" ? t.settlement15m : t.settlement60m,
        detail:
          condition.detail ??
          (condition.interval === "15m"
            ? t.settlement15mDetail
            : t.settlement60mDetail),
        tone: "neutral",
        icon: "clock",
      };
    case "fixedFormula":
      return {
        label: t.fixedFormula,
        detail:
          condition.detail ??
          (condition.until !== undefined
            ? t.fixedFormulaUntilDetail(formatDateLabel(condition.until))
            : condition.years !== undefined
              ? t.fixedFormulaYearsDetail(condition.years)
              : t.fixedFormulaDetail),
        tone: "neutral",
        icon: "lock",
      };
    case "noDeposit":
      return {
        label: t.noDeposit,
        detail: condition.detail ?? t.noDepositDetail,
        tone: "positive",
        icon: null,
      };
    case "discount":
    case "bonus":
      return {
        label: condition.label,
        detail: condition.detail,
        tone: "positive",
        icon: null,
      };
    case "membership":
      return {
        label: t.membership,
        detail: condition.detail,
        tone: "neutral",
        icon: "members",
      };
    case "limitedArea":
      return {
        label: t.limitedArea,
        detail: condition.detail,
        tone: "neutral",
        icon: "document",
      };
    case "contractTerm":
      return {
        label: t.contractTerm(condition.years),
        detail: condition.detail ?? t.contractTermDetail(condition.years),
        tone: "neutral",
        icon: "document",
      };
    case "note":
      return {
        label: condition.label,
        detail: condition.detail,
        tone: condition.positive ? "positive" : "neutral",
        icon: condition.positive ? null : "document",
      };
  }
}
